/**
 * DropdownFooterAction - the call-to-action row pinned under a dropdown list.
 *
 * Atomic Design role: Atom. SearchableSelect and AsyncSearchableSelect both
 * render one when given `footerAction`, so "the person you're looking for
 * isn't here? → + Quick Add User" sits right where the search came up empty,
 * not only under the field. It stays visible whatever the list shows —
 * results, "Searching...", or "No results" — because the empty list is exactly
 * when it's needed.
 *
 * Fires on mousedown (with preventDefault) for the same reason the options
 * do: a click would land after the input's blur had already closed the list.
 *
 * @example
 * <DropdownFooterAction label="+ Quick Add User" onSelect={() => openQuickAdd(searchText)} />
 */
import React from 'react';

/** What a dropdown's footer action does. `onSelect` receives whatever the
 *  user had typed, so the action can carry it on (e.g. pre-fill a name). */
export interface DropdownFooterActionConfig {
	/** Row text, e.g. "+ Quick Add User". */
	label: string;
	/** Called with the current search text when the row is chosen. */
	onSelect: (searchText: string) => void;
}

export interface DropdownFooterActionProps {
	label: string;
	/** Called when the row is pressed (mouse, touch or keyboard). */
	onSelect: () => void;
	/** Overrides the atom's data-ref so a composing component keeps its identity. */
	dataRef?: string;
}

export const DropdownFooterAction: React.FC<DropdownFooterActionProps> = ({ label, onSelect, dataRef = 'atom-dropdown-footer-action' }) => (
	<button
		type="button"
		/* mousedown, not click — see the docblock. */
		onMouseDown={(e) => { e.preventDefault(); onSelect(); }}
		/* Keyboard users reach it with Tab; Enter/Space fire click, not mousedown. */
		onClick={(e) => { if (e.detail === 0) onSelect(); }}
		className="dropdown-footer-action flex items-center w-full min-h-44px px-12px py-8px border-t border-brand-border text-left text-14px font-sans font-semibold text-brand-primary bg-white hover:bg-brand-surface focus:outline-none focus-visible:bg-brand-surface transition-colors"
		data-ref={dataRef}
	>
		{label}
	</button>
);

export default DropdownFooterAction;
